/**
 * Helper para registrar y recuperar la frecuencia de uso de los menús en localStorage.
 * Garantiza que solo se retornen menús que existan actualmente en la lista recibida desde el backend.
 */

const STORAGE_PREFIX = 'level_desk_visited_menus_';

/**
 * Registra una visita para un idMenu específico del usuario actual.
 * @param {number|string} menuId - ID del menú
 * @param {string} username - Nombre de usuario / preferred_username
 */
export const recordMenuVisit = (menuId, username = 'default') => {
  if (!menuId && menuId !== 0) return;
  if (menuId === 0) return; // No contar Home como acceso directo

  try {
    const key = `${STORAGE_PREFIX}${username}`;
    const raw = localStorage.getItem(key);
    const stored = raw ? JSON.parse(raw) : {};

    stored[menuId] = (stored[menuId] || 0) + 1;
    localStorage.setItem(key, JSON.stringify(stored));
  } catch (error) {
    console.error('Error registrando visita al menú en localStorage:', error);
  }
};

/**
 * Obtiene los menús más visitados que se encuentren actualmente activos en menuList del backend.
 * @param {Array} menuList - Lista de menús activos desde el backend
 * @param {string} username - Nombre de usuario
 * @param {number} limit - Cantidad máxima de accesos frecuentes a retornar
 * @returns {Array} Lista de menús ordenados por visitas descendente
 */
export const getFrequentMenus = (menuList, username = 'default', limit = 4) => {
  if (!menuList || !Array.isArray(menuList)) return [];

  try {
    const key = `${STORAGE_PREFIX}${username}`;
    const raw = localStorage.getItem(key);
    if (!raw) return [];

    const stored = JSON.parse(raw);

    // Aplanar y recolectar todos los menús válidos que vengan del backend
    const activeMenusMap = new Map();

    const collectActiveMenus = (items) => {
      items.forEach((item) => {
        if (item.idMenu && item.idMenu !== 0 && item.visible !== false) {
          activeMenusMap.set(item.idMenu, item);
        }
        if (item.menus && Array.isArray(item.menus)) {
          collectActiveMenus(item.menus);
        }
      });
    };

    collectActiveMenus(menuList);

    // Filtrar únicamente los menús que existen activamente en el backend
    const frequentItems = [];
    Object.keys(stored).forEach((idMenuStr) => {
      const idMenu = isNaN(idMenuStr) ? idMenuStr : Number(idMenuStr);
      const menuItem = activeMenusMap.get(idMenu) || activeMenusMap.get(idMenuStr);

      if (menuItem) {
        frequentItems.push({
          ...menuItem,
          visitCount: stored[idMenuStr] || 0,
        });
      }
    });

    // Ordenar de mayor a menor visitas
    frequentItems.sort((a, b) => b.visitCount - a.visitCount);

    return frequentItems.slice(0, limit);
  } catch (error) {
    console.error('Error obteniendo menús frecuentes de localStorage:', error);
    return [];
  }
};

/**
 * Limpia el historial de visitas de menús para un usuario.
 * @param {string} username
 */
export const clearFrequentMenus = (username = 'default') => {
  try {
    localStorage.removeItem(`${STORAGE_PREFIX}${username}`);
  } catch (error) {
    console.error('Error limpiando historial de menús:', error);
  }
};
